import * as A from 'astronomy-engine';
import { effect, signal } from '@preact/signals';
import type { Place } from '../astro/frame';
import { eyeLimitingMagnitude, eyeSkyBrightness } from '../astro/skybrightness';
import { EXT_K, eyeMagAt, type EyeSky } from '../render/nakedeye';
import { DEG, type Vec3 } from '../lib/math';
import { loadJSON, place, saveJSON, timeLive } from '../state';

// What the person's own eyes say about the sky: the darkness check and "I can't see it". The naked-eye view follows
// these over the light pollution map's estimate.
//
// The check finds the faintest star someone can see (between the faintest one seen and the next fainter one not
// seen), then works back through the sky brightness model (astro/skybrightness.ts) to the light pollution that gives
// that limit with that night's Sun and Moon. That is kept for the place; on other nights the model adds their own
// Moon and twilight to it, so one check per place is usually enough. "I can't see it" and "I can see more stars than
// this" only nudge tonight's limit, until the next sunrise.

/** Saved data checks a number before it is used (saved data is untrusted). */
const within = (v: unknown, lo: number, hi: number): v is number => typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi;

// ---------- Places ----------
/** A check belongs to a place: everywhere within this distance of where it was made (so a place is about 5 km across). */
export const PLACE_KM = 2.5;
export function distanceKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const dLon = ((((b.lon - a.lon) % 360) + 540) % 360) - 180;
  const x = dLon * Math.cos(((a.lat + b.lat) / 2) * DEG);
  return 111.2 * Math.hypot(b.lat - a.lat, x);
}
const near = (a: { lat: number; lon: number }, b: { lat: number; lon: number }) => distanceKm(a, b) <= PLACE_KM;

// ---------- The eye's limit across the sky ----------
// Naked eye's own test (render/nakedeye.ts), so the check rings and judges stars just as the sky draws them: dimmed by
// the air at their height, against the sky's glow where they are (brighter low down and near the Moon).
export { EXT_K };
/** As the eye sees something of magnitude `mag` in direction `enu` (east, north, up) under tonight's sky `sky`: its
 *  magnitude as if it were overhead. */
export const eyeMag = (mag: number, enu: Vec3, sky: EyeSky) => eyeMagAt(mag, enu[0], enu[1], enu[2], sky);

// ---------- Light pollution from what the eye sees ----------
/** No light pollution at all: the natural night sky overhead (mag/arcsec²). */
export const SQM_DARKEST = 22;
/** Brighter than any city centre's sky on a clear night. */
export const SQM_BRIGHTEST = 16;
/** The Sun and Moon tonight, as the sky brightness model takes them (degrees, and the Moon's magnitude). */
export interface SkyNow { sunAlt: number; moonAlt: number; moonMag: number }

/**
 * The light pollution that makes the model's naked-eye limit overhead equal `limit` with this Sun and Moon: the
 * zenith brightness without the Sun or Moon (artificial light over the natural night sky), in the same terms as the
 * map's estimate. Held between the brightest and darkest plausible skies.
 */
export function sqmForLimit(limit: number, sky: SkyNow): number {
  const at = (sqm: number) => eyeLimitingMagnitude(sky.sunAlt, sky.moonAlt, sky.moonMag, 7, sqm);
  let lo = SQM_BRIGHTEST, hi = SQM_DARKEST;
  // Fainter than even an unpolluted sky allows tonight (or no number at all): the darkest. Brighter than the brightest.
  if (!(at(hi) > limit)) return hi;
  if (at(lo) >= limit) return lo;
  for (let k = 0; k < 48; k++) {
    const mid = (lo + hi) / 2;
    if (at(mid) < limit) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/**
 * How much of the sky's brightness tonight's twilight and Moon give, against light pollution `sqm`: 0 none, 1 all.
 * Where they give most of it, a check says little about the light pollution, so it waits for a darker sky.
 */
export function moonTwilightShare(sky: SkyNow, sqm: number): number {
  const f = 10 ** (-0.4 * eyeSkyBrightness(sky.sunAlt, sky.moonAlt, sky.moonMag, 1, 99));
  return f / (f + 10 ** (-0.4 * sqm));
}
/** Above this share, the check is not offered and says to wait (twilight and moonlight over 1.5 times the rest). */
export const MOON_TWILIGHT_MAX = 0.6;

/** Why a check's answer is kept for tonight only, not for the place. */
export type TonightOnly = 'moon' | 'twilight' | 'dark' | 'bright';
/**
 * What a check's answer `found` (the limit overhead) means: the light pollution it implies, the limit that gives
 * tonight, and whether it can be kept for the place. It is kept only when it says something about the light
 * pollution; otherwise it is for tonight only, because the Moon or twilight gave too much of the sky's light to tell
 * (judged with the light pollution the answer implies, not the map's guess), or because it went past what any sky
 * allows tonight: more than with no light pollution at all ('dark'), or fewer than a city centre's sky ('bright':
 * haze, cloud, or eyes not yet used to the dark).
 */
export function judgeCheck(found: number, sky: SkyNow): { sqm: number; limit: number; tonight: TonightOnly | null } {
  const sqm = sqmForLimit(found, sky);
  const limit = eyeLimitingMagnitude(sky.sunAlt, sky.moonAlt, sky.moonMag, 7, sqm);
  const tonight = limit < found - 0.05 ? 'dark' : limit > found + 0.05 ? 'bright'
    : moonTwilightShare(sky, sqm) <= MOON_TWILIGHT_MAX ? null
    : moonTwilightShare({ ...sky, moonAlt: -90 }, sqm) > MOON_TWILIGHT_MAX ? 'twilight' : 'moon';
  return { sqm, limit, tonight };
}
/** The Sun this far down (degrees, nautical dusk): dark enough for a check to say much. */
export const DARK_ENOUGH = -12;

// ---------- Working out the answers ----------
/**
 * What a check has learnt: the faintest star seen and the brightest not seen, each as the eye sees it at its
 * altitude (as if overhead); null until known.
 */
export interface Bounds { seen: number | null; unseen: number | null }
export const NO_BOUNDS: Bounds = { seen: null, unseen: null };
/** An answer to "Which is the faintest one you can see?": a star (its place in the round, brightest first), or none, or all. */
export type Answer = number | 'none' | 'all';
/** The rounds a check may take: one, then once more after "None of them" or "All of them". */
export const ROUNDS = 2;

/** What an answer adds, for a round of stars as the eye sees them (`effs`, brightest first). */
export function applyAnswer(b: Bounds, effs: number[], a: Answer): Bounds {
  let { seen, unseen } = b;
  const see = (m: number) => (seen = seen == null ? m : Math.max(seen, m));
  const miss = (m: number) => (unseen = unseen == null ? m : Math.min(unseen, m));
  if (a === 'none') miss(effs[0]);
  else if (a === 'all') see(effs[effs.length - 1]);
  else {
    see(effs[a]);
    if (a + 1 < effs.length) miss(effs[a + 1]);
  }
  return { seen, unseen };
}

/** Half the step between the stars of a round: the limit is taken this far past an edge that is known on one side only. */
const HALF_STEP = 0.25;
export type Settled = { limit: number } | { again: 'fainter' | 'brighter'; centre: number } | { fail: true };
/**
 * Where a check stands after `round` rounds: the limit (half way between the faintest seen and the brightest not seen),
 * another round fainter or brighter, or no answer (not even the brightest seen, twice: cloud, haze or eyes not yet
 * used to the dark). Answers that disagree still meet half way.
 */
export function settle(b: Bounds, round: number): Settled {
  if (b.seen != null && b.unseen != null) return { limit: (b.seen + b.unseen) / 2 };
  if (round < ROUNDS) {
    // Two magnitudes beyond the edge found, starting a quarter of a magnitude past it.
    return b.seen != null ? { again: 'fainter', centre: b.seen + 1.25 } : { again: 'brighter', centre: b.unseen! - 1.25 };
  }
  if (b.seen != null) return { limit: b.seen + HALF_STEP };
  return { fail: true };
}

// ---------- Choosing the stars ----------
export interface CheckStar {
  /** Index in the star catalogue. */
  i: number;
  mag: number;
  /** As the eye sees it where it is (the air and the sky's glow there): its magnitude as if overhead. */
  eff: number;
  /** True altitude and azimuth (degrees). */
  alt: number;
  az: number;
  /** Where it is on the screen (CSS px) when chosen. */
  x: number;
  y: number;
}
export interface StarSource {
  count: number;
  /** Brightest first, as the catalogue is sorted. */
  mag: ArrayLike<number>;
  /** J2000 unit vectors, xyz interleaved. */
  dir: ArrayLike<number>;
  info?: Map<number, { variable?: string; hd?: number }>;
}
export interface GatherInput {
  cat: StarSource;
  toEnu: (eqj: Vec3) => Vec3;
  project: (enu: Vec3) => { x: number; y: number } | null;
  /** The part of the screen the stars may be in (CSS px). */
  region: { x0: number; y0: number; x1: number; y1: number };
  /** Tonight's sky for the eye, as Naked eye draws it (render/nakedeye.ts). */
  eye: EyeSky;
  /** As the eye sees them (as if overhead): the faintest and brightest wanted. */
  effRange: [number, number];
  /** Lowest true altitude (degrees). */
  minAlt: number;
  /** Directions to keep away from (ENU), each with a radius in degrees: the Moon, the bright planets. */
  avoid: { enu: Vec3; radius: number }[];
}
/** Stars only this far from a star no more than a magnitude fainter can be told apart at a glance. */
const ALONE_DEG = 0.35;
/**
 * Greek-lettered stars whose brightness swings by about half a magnitude or more, by HD number: Algol, Mira, Betelgeuse,
 * Rasalgethi, γ Cas, δ Cep, μ Cep, η Aql, β Lyr, ρ Cas, χ Cyg, ε Aur, λ Tau, ζ Gem, η Gem, β Peg, Antares, δ Sco,
 * δ Lib, ψ¹ Aur, ρ Per and τ⁴ Ser. (The catalogue marks every star ever found to vary, Altair and Deneb among them,
 * however slightly, and holds no ranges.)
 */
const SWINGING_HD = new Set([19356, 14386, 39801, 156014, 5394, 213306, 206936, 187929, 174638, 224014, 187796, 31964, 25204, 52973, 42995, 217906, 148478, 143275, 132742, 44537, 19058, 139216]);
const GREEK = /^(Alp|Bet|Gam|Del|Eps|Zet|Eta|The|Iot|Kap|Lam|Mu|Nu|Xi|Omi|Pi|Rho|Sig|Tau|Ups|Phi|Chi|Psi|Ome)(-\d)?$/;
/** Whether a star's catalogue magnitude could mislead a check: one of those above, or a star known only by its
 *  variable-star name (R Hya, W Cyg, V695 Cyg), left out as a precaution. */
export function swings(s: { variable?: string; hd?: number } | undefined): boolean {
  if (!s?.variable) return false;
  return !GREEK.test(s.variable) || SWINGING_HD.has(s.hd ?? 0);
}
/** The catalogue's stars in cells of the unit sphere about 0.6° wide (wider than `ALONE_DEG`), so the stars near one are
 *  found by looking in its cell and the 26 around it. Built once for each catalogue. */
const CELL = 0.01;
const cellKey = (x: number, y: number, z: number) => ((Math.floor(x / CELL) + 128) * 256 + Math.floor(y / CELL) + 128) * 256 + Math.floor(z / CELL) + 128;
const grids = new WeakMap<ArrayLike<number>, Map<number, number[]>>();
function gridOf(cat: StarSource): Map<number, number[]> {
  let g = grids.get(cat.dir);
  if (!g) {
    g = new Map();
    for (let i = 0; i < cat.count; i++) {
      const k = cellKey(cat.dir[i * 3], cat.dir[i * 3 + 1], cat.dir[i * 3 + 2]);
      const list = g.get(k);
      if (list) list.push(i);
      else g.set(k, [i]);
    }
    grids.set(cat.dir, g);
  }
  return g;
}
/** Whether star `i` has no star brighter than a magnitude fainter than it within `ALONE_DEG`. */
function alone(cat: StarSource, grid: Map<number, number[]>, i: number, cosAlone: number): boolean {
  const x = cat.dir[i * 3], y = cat.dir[i * 3 + 1], z = cat.dir[i * 3 + 2], m = cat.mag[i] + 1;
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      for (let dz = -1; dz <= 1; dz++) {
        const list = grid.get(cellKey(x + dx * CELL, y + dy * CELL, z + dz * CELL));
        if (!list) continue;
        for (const j of list) {
          if (j !== i && cat.mag[j] < m && cat.dir[j * 3] * x + cat.dir[j * 3 + 1] * y + cat.dir[j * 3 + 2] * z > cosAlone) return false;
        }
      }
    }
  }
  return true;
}
/** Where the screen has a star of the brightness wanted, well up, away from the Moon and planets, on its own and steady. */
export function gatherCheckStars(g: GatherInput): CheckStar[] {
  const { cat } = g;
  const out: CheckStar[] = [];
  const v: Vec3 = [0, 0, 0];
  const cosAlone = Math.cos(ALONE_DEG * DEG);
  const grid = gridOf(cat);
  const avoid = g.avoid.map((a) => ({ enu: a.enu, cos: Math.cos(a.radius * DEG) }));
  // An eye magnitude is fainter than the catalogue's (the air only dims), but for a part of the sky darker than
  // overhead, well away from a bright Moon: no more than `minLoss` brighter.
  const faintest = g.effRange[1] - g.eye.minLoss;
  for (let i = 0; i < cat.count; i++) {
    const m = cat.mag[i];
    if (m > faintest) break;
    v[0] = cat.dir[i * 3];
    v[1] = cat.dir[i * 3 + 1];
    v[2] = cat.dir[i * 3 + 2];
    const enu = g.toEnu(v);
    const alt = Math.asin(Math.max(-1, Math.min(1, enu[2]))) / DEG;
    if (alt < g.minAlt) continue;
    const eff = eyeMag(m, enu, g.eye);
    if (eff < g.effRange[0] || eff > g.effRange[1]) continue;
    if (swings(cat.info?.get(i))) continue;
    if (avoid.some((a) => a.enu[0] * enu[0] + a.enu[1] * enu[1] + a.enu[2] * enu[2] > a.cos)) continue;
    const p = g.project(enu);
    if (!p || p.x < g.region.x0 || p.x > g.region.x1 || p.y < g.region.y0 || p.y > g.region.y1) continue;
    if (!alone(cat, grid, i, cosAlone)) continue;
    const az = ((Math.atan2(enu[0], enu[1]) / DEG) % 360 + 360) % 360;
    out.push({ i, mag: m, eff, alt, az, x: p.x, y: p.y });
  }
  return out;
}

/**
 * Four or five stars for one round (`fewest` lets a second round, where the brightest stars are few, make do with
 * three): spread over about two magnitudes around `centre` (as the eye sees them), at least a third of a magnitude
 * apart, well apart on the screen (`minSep` CSS px) and spread across it, the higher the better. Brightest first; empty
 * when the view has too few.
 */
export function chooseCheckStars(cands: CheckStar[], centre: number, opts: { span?: number; minSep?: number; fewest?: number } = {}): CheckStar[] {
  const span = opts.span ?? 2, minSep = opts.minSep ?? 72, fewest = opts.fewest ?? 4;
  for (let n = 5; n >= Math.max(3, fewest); n--) {
    const step = span / (n - 1);
    const tol = step / 2 - 0.05;
    const levels = Array.from({ length: n }, (_, k) => centre - span / 2 + step * k);
    // The levels nearest the expected limit matter most: they choose first, then outwards.
    const order = levels.map((_, k) => k).sort((a, b) => Math.abs(a - (n - 1) / 2) - Math.abs(b - (n - 1) / 2));
    const out: CheckStar[] = [];
    for (const k of order) {
      let best: CheckStar | null = null, bestScore = Infinity;
      for (const c of cands) {
        const off = Math.abs(c.eff - levels[k]);
        if (off > tol) continue;
        let gap = Infinity, clash = false;
        for (const o of out) {
          const d = Math.hypot(o.x - c.x, o.y - c.y);
          if (d < minSep || Math.abs(o.eff - c.eff) < 0.3) clash = true;
          gap = Math.min(gap, d);
        }
        if (clash) continue;
        const score = off / tol - (out.length ? Math.min(gap, 260) / 260 : 0) * 0.8 - (c.alt >= 30 ? 0.4 : 0);
        if (score < bestScore) {
          bestScore = score;
          best = c;
        }
      }
      if (best) out.push(best);
    }
    if (out.length >= fewest) return out.sort((a, b) => a.eff - b.eff);
  }
  return [];
}

// ---------- Checks, kept for each place ----------
export interface SkyCheck {
  lat: number;
  lon: number;
  /** Light pollution worked out from it (mag/arcsec², as `sqmForLimit` gives it). */
  sqm: number;
  /** The naked-eye limit found, overhead (magnitude). */
  limit: number;
  /** When (ms since 1970). */
  t: number;
}
/** The most checks kept: the newest. */
const MAX_CHECKS = 40;
export function cleanCheck(v: unknown, now = Date.now()): SkyCheck | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const c = v as Record<string, unknown>;
  if (!within(c.lat, -90, 90) || !within(c.lon, -180, 180) || !within(c.sqm, SQM_BRIGHTEST, SQM_DARKEST) || !within(c.limit, -2, 9) || !within(c.t, 0, now + 86400e3)) return null;
  return { lat: c.lat, lon: c.lon, sqm: c.sqm, limit: c.limit, t: c.t };
}
/** Saved checks, each checked: a bad one is dropped, never a crash. */
export function cleanChecks(v: unknown, now = Date.now()): SkyCheck[] {
  if (!Array.isArray(v)) return [];
  return v.map((c) => cleanCheck(c, now)).filter((c): c is SkyCheck => !!c).slice(-MAX_CHECKS);
}
export const skyChecks = signal<SkyCheck[]>(cleanChecks(loadJSON<unknown>('skyChecks', [])));

/** The check that covers a place: the nearest within `PLACE_KM`. */
export function checkAt(p: { lat: number; lon: number }, list = skyChecks.peek()): SkyCheck | null {
  let best: SkyCheck | null = null, bestD = PLACE_KM;
  for (const c of list) {
    const d = distanceKm(p, c);
    if (d <= bestD) {
      best = c;
      bestD = d;
    }
  }
  return best;
}

/** Keep a check: it replaces any other for the same place, and tonight's nudges give way to it. */
export function saveCheck(c: SkyCheck) {
  const list = [...skyChecks.peek().filter((x) => !near(x, c)), c].slice(-MAX_CHECKS);
  skyChecks.value = list;
  saveJSON('skyChecks', list);
  const t = eyeTonight.peek();
  if (t && near(t, c)) setTonight(null);
}

/**
 * Forget every check that covers a place (two can, where their places overlap), so the map's estimate stands there
 * again; returns how to put them back (null where there is none). Putting back keeps whatever has been saved since: a
 * newer check for the same place wins, and the store keeps its newest `MAX_CHECKS`.
 */
export function forgetCheck(p: { lat: number; lon: number }): (() => void) | null {
  const removed = skyChecks.peek().filter((c) => near(c, p));
  if (!removed.length) return null;
  const list = skyChecks.peek().filter((c) => !near(c, p));
  skyChecks.value = list;
  saveJSON('skyChecks', list);
  return () => {
    const now = skyChecks.peek();
    const back = removed.filter((c) => !now.some((x) => near(x, c) && x.t >= c.t));
    const merged = [...now, ...back].sort((a, b) => a.t - b.t).slice(-MAX_CHECKS);
    skyChecks.value = merged;
    saveJSON('skyChecks', merged);
  };
}

let cached: { p: Place | null; list: SkyCheck[] | null; v: number | null } = { p: null, list: null, v: null };
/**
 * The light pollution for what the eye can see at `p`: the darkness check's there, else the map's when that is on
 * (else null: the Bortle class chosen in Settings). Read in a component, it follows new checks.
 */
export function eyeSqm(p: Place, set: { bortleAuto: boolean; sqm: number | null }): number | null {
  void skyChecks.value;
  return measuredSqm(p) ?? (set.bortleAuto ? set.sqm : null);
}

/** Light pollution measured at a place by the darkness check, as zenith sky brightness (mag/arcsec²) without the Sun
 *  or Moon, in the map's terms; null where there is no check, so the map's estimate stands. Called every frame: it
 *  works the answer out again only when the place or the checks change. */
export function measuredSqm(p: Place): number | null {
  const list = skyChecks.peek();
  if (cached.p !== p || cached.list !== list) cached = { p, list, v: checkAt(p, list)?.sqm ?? null };
  return cached.v;
}

// ---------- Tonight's nudge ----------
/** "I can't see it" and "I can see more" for one night at one place, until sunrise there. */
export interface Tonight {
  /** Magnitudes added to the limit (negative: fewer stars). */
  nudge: number;
  /** When it ends: the next sunrise (ms). */
  until: number;
  lat: number;
  lon: number;
  /** "I can't see it" taps tonight, and whether the check has been offered again for them. */
  taps: number;
  reoffered: boolean;
}
/** The furthest a nudge goes either way (magnitudes). */
export const NUDGE_MIN = -6, NUDGE_MAX = 1.5;
/** "I can't see it" on something brighter than this (as the eye sees it) says cloud or something in the way, rather
 *  than hide nearly every star. */
export const BRIGHT_FLOOR = 1.5;
/** "I can see more stars than this": half a magnitude fainter each time. */
export const MORE_STEP = 0.5;

export function cleanTonight(v: unknown, now = Date.now()): Tonight | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const t = v as Record<string, unknown>;
  if (!within(t.nudge, NUDGE_MIN, NUDGE_MAX) || !within(t.until, now, now + 2 * 86400e3) || !within(t.lat, -90, 90) || !within(t.lon, -180, 180)) return null;
  return { nudge: t.nudge, until: t.until, lat: t.lat, lon: t.lon, taps: within(t.taps, 0, 1000) ? Math.round(t.taps) : 0, reoffered: t.reoffered === true };
}
/** Tonight's nudges, if any (gone after sunrise). */
export const eyeTonight = signal<Tonight | null>(cleanTonight(loadJSON<unknown>('eyeTonight', null)));
function setTonight(t: Tonight | null) {
  eyeTonight.value = t;
  saveJSON('eyeTonight', t);
}

/** Tonight's nudge to the naked-eye limit from "I can't see it" and "I can see more", in magnitudes (negative: fewer
 *  stars). It applies to the sky as it is now at the place where it was given, until the next sunrise there: not to
 *  another time on show, nor another place. */
export const limitNudge = signal(0);
/** "I can't see it" taps tonight, here. */
export const cantSeeTaps = signal(0);
/** Whether tonight's nudges belong to a place (and are not over). */
const tonightAt = (t: Tonight | null, p: { lat: number; lon: number }, now = Date.now()): t is Tonight => !!t && now < t.until && near(t, p);
effect(() => {
  const t = eyeTonight.value, p = place.value, live = timeLive.value;
  const on = tonightAt(t, p);
  limitNudge.value = on && live ? t.nudge : 0;
  cantSeeTaps.value = on ? t.taps : 0;
});

/** The next sunrise at a place after `now` (ms); half a day on where the Sun does not rise. */
export function nextSunrise(p: { lat: number; lon: number }, now: number): number {
  try {
    const r = A.SearchRiseSet(A.Body.Sun, new A.Observer(p.lat, p.lon, 0), +1, new Date(now), 2);
    if (r) return r.date.getTime();
  } catch {
    /* fall through */
  }
  return now + 12 * 3600e3;
}

/** Tonight here: the nudges already given, or a fresh night. */
function tonightHere(p: { lat: number; lon: number }, now: number): Tonight {
  const t = eyeTonight.peek();
  return tonightAt(t, p, now) ? t : { nudge: 0, until: nextSunrise(p, now), lat: p.lat, lon: p.lon, taps: 0, reoffered: false };
}

/**
 * "I can't see it", for something seen by the eye as magnitude `eff` (as if overhead): tonight's limit goes to just
 * brighter than it. `base` is tonight's limit before any nudge. Something brighter than `BRIGHT_FLOOR` leaves the
 * limit alone (`nudged` false). Either way the tap counts towards offering the check again; `undo` puts things back.
 */
export function cantSee(eff: number, base: number, p: { lat: number; lon: number }, now = Date.now()): { nudged: boolean; undo: () => void } {
  const before = eyeTonight.peek();
  const t = tonightHere(p, now);
  const target = eff - 0.1;
  const nudged = target >= BRIGHT_FLOOR;
  const nudge = nudged ? Math.max(NUDGE_MIN, Math.min(t.nudge, target - base)) : t.nudge;
  setTonight({ ...t, nudge, taps: t.taps + 1 });
  return { nudged, undo: () => setTonight(before) };
}

/** "I can see more stars than this": tonight's limit half a magnitude fainter, up to `NUDGE_MAX` (null when it is
 *  there already); returns how to undo it. */
export function seeMore(p: { lat: number; lon: number }, now = Date.now()): (() => void) | null {
  const before = eyeTonight.peek();
  const t = tonightHere(p, now);
  if (t.nudge >= NUDGE_MAX) return null;
  setTonight({ ...t, nudge: Math.min(NUDGE_MAX, t.nudge + MORE_STEP) });
  return () => setTonight(before);
}

/** A check whose answer cannot be kept for the place (the Moon or twilight drowned it, or it went past what any sky
 *  allows): tonight's limit set to it instead, as a nudge from `base`, tonight's limit without one. */
export function nudgeTonightTo(limit: number, base: number, p: { lat: number; lon: number }, now = Date.now()) {
  const t = tonightHere(p, now);
  setTonight({ ...t, nudge: Math.max(NUDGE_MIN, Math.min(NUDGE_MAX, limit - base)) });
}

/** Back to tonight's limit with no nudge (the taps still count). */
export function resetNudge() {
  const t = eyeTonight.peek();
  if (t) setTonight({ ...t, nudge: 0 });
}

/** The check has been offered again tonight, after "I can't see it". */
export function markReoffered(p: { lat: number; lon: number }, now = Date.now()) {
  setTonight({ ...tonightHere(p, now), reoffered: true });
}

/** Ends tonight's nudges at sunrise, while Ahillah is open (and as it comes back). Returns how to stop. */
export function watchTonight(): () => void {
  const check = () => {
    const t = eyeTonight.peek();
    if (t && Date.now() >= t.until) setTonight(null);
  };
  check();
  const id = setInterval(check, 60e3);
  const onVis = () => document.visibilityState === 'visible' && check();
  document.addEventListener('visibilitychange', onVis);
  return () => {
    clearInterval(id);
    document.removeEventListener('visibilitychange', onVis);
  };
}

// ---------- The offer ----------
interface Offered { lat: number; lon: number; t: number }
const MAX_OFFERED = 60;
export function cleanOffered(v: unknown, now = Date.now()): Offered[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((o): o is Offered => !!o && typeof o === 'object' && within(o.lat, -90, 90) && within(o.lon, -180, 180) && within(o.t, 0, now + 86400e3))
    .map((o) => ({ lat: o.lat, lon: o.lon, t: o.t }))
    .slice(-MAX_OFFERED);
}
/** Places where the check has been offered (once each). */
export const offeredPlaces = signal<Offered[]>(cleanOffered(loadJSON<unknown>('skyCheckOffers', [])));
export const offeredAt = (p: { lat: number; lon: number }, list = offeredPlaces.peek()) => list.some((o) => near(o, p));
export function markOffered(p: { lat: number; lon: number }, now = Date.now()) {
  if (offeredAt(p)) return;
  const list = [...offeredPlaces.peek(), { lat: p.lat, lon: p.lon, t: now }].slice(-MAX_OFFERED);
  offeredPlaces.value = list;
  saveJSON('skyCheckOffers', list);
}

export interface OfferState {
  nakedEye: boolean;
  /** The sky on show is here and now: live time, and no other place or saved night. */
  hereAndNow: boolean;
  /** The Sun's altitude where the person is (degrees). */
  sunAlt: number;
  /** Tonight's twilight and Moon would drown what the check measures (`moonTwilightShare` over `MOON_TWILIGHT_MAX`). */
  tooBright: boolean;
  checkedHere: boolean;
  offeredHere: boolean;
  cantSeeTaps: number;
  reofferedTonight: boolean;
  /** Something else is on screen: Help's walkthrough, a film, an open sheet or card, another offer, the check itself. */
  busy: boolean;
}
/**
 * Whether to offer the check now: the first time Ahillah is used after dark at a place ('first'), and again after two
 * "I can't see it" taps in one night ('again'). Only in Naked eye, for the sky here and now, once it is dark enough to
 * tell, and never over anything else.
 */
export function offerDue(o: OfferState): 'first' | 'again' | null {
  if (!o.nakedEye || !o.hereAndNow || o.busy || o.sunAlt > DARK_ENOUGH || o.tooBright) return null;
  if (o.cantSeeTaps >= 2 && !o.reofferedTonight) return 'again';
  if (o.checkedHere || o.offeredHere) return null;
  return 'first';
}

/** Why the check is offered again: "I can't see it" taps tonight, a new place this far (km) from the last check made
 *  elsewhere, or a new place when it was only offered elsewhere before. */
export type AgainWhy = { taps: number } | { km: number } | { newPlace: true };
/** Why an offer due at `p` (offerDue) comes again, or null when it is the first time the check is offered at all. */
export function offerAgainWhy(due: 'first' | 'again', p: { lat: number; lon: number }, taps: number, checks = skyChecks.peek(), offers = offeredPlaces.peek()): AgainWhy | null {
  if (due === 'again') return { taps };
  let last: SkyCheck | null = null;
  for (const c of checks) if (!near(c, p) && (!last || c.t > last.t)) last = c;
  if (last) return { km: distanceKm(p, last) };
  return offers.some((o) => !near(o, p)) ? { newPlace: true } : null;
}
